import { Button, Col, Form, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import Select from "react-select"

export default function LotForm({state, handleInputChange, submit, isAddingOrEditing, isRegistering, zoneOptions = []}) {
	const { t } = useTranslation()

	const zones = zoneOptions?.map(zone => ({
		value: zone._id,
		label: zone.name
	}))

	const typeOptions = [
		{ value: 'semental', label: t('admin.lot.type_semental') },
		{ value: 'vacas', label: t('admin.lot.type_vacas') },
		{ value: 'novillas', label: t('admin.lot.type_novillas') },
		{ value: 'rebaño', label: t('admin.lot.type_rebano') },
		{ value: 'terneros', label: t('admin.lot.type_terneros') },
		{ value: 'cebo', label: t('admin.lot.type_cebo') },
		{ value: 'lazareto', label: t('admin.lot.type_lazareto') },
		{ value: 'cuarentena', label: t('admin.lot.type_cuarentena') }
	]

	const handlingOptions = [
		{ value: 'reposo', label: t('admin.lot.handling_reposo') },
		{ value: 'paridera_continua', label: t('admin.lot.handling_paridera_continua') },
		{ value: 'paridera_estacionada', label: t('admin.lot.handling_paridera_estacionada') },
		{ value: 'reproduccion_asistida', label: t('admin.lot.handling_reproduccion_asistida') },
		{ value: 'terneros_destetados', label: t('admin.lot.handling_terneros_destetados') },
		{ value: 'apartados_venta', label: t('admin.lot.handling_apartados_venta') },
		{ value: 'recria', label: t('admin.lot.handling_recria') },
		{ value: 'cebo_terneros', label: t('admin.lot.handling_cebo_terneros') },
		{ value: 'cebo_desvieje', label: t('admin.lot.handling_cebo_desvieje') },
		{ value: 'cebo_rebano', label: t('admin.lot.handling_cebo_rebano') }
	]

	const handlingByType = {
		semental: ['reposo'],
		vacas: ['paridera_continua', 'paridera_estacionada', 'reproduccion_asistida'],
		novillas: ['paridera_continua', 'paridera_estacionada', 'reproduccion_asistida'],
		rebaño: ['paridera_continua', 'paridera_estacionada', 'reproduccion_asistida'],
		terneros: ['terneros_destetados', 'apartados_venta', 'recria'],
		cebo: ['cebo_terneros', 'cebo_desvieje', 'cebo_rebano'],
		lazareto: [],
		cuarentena: []
	}

	const filteredHandlingOptions = state.type
		? handlingOptions.filter(option => handlingByType[state.type]?.includes(option.value))
		: [];

	const phaseByHandling = {
		paridera_estacionada: [
			{ value: 'cubricion', label: t('admin.lot.handlingPhase_cubricion') },
			{ value: 'postcubricion', label: t('admin.lot.handlingPhase_postcubricion') }
		],
		reproduccion_asistida: [
			{ value: 'sincronizacion', label: t('admin.lot.handlingPhase_sincronizacion') },
			{ value: 'inseminadas', label: t('admin.lot.handlingPhase_inseminadas') },
			{ value: 'post_ia', label: t('admin.lot.handlingPhase_post_ia') },
			{ value: 'repaso', label: t('admin.lot.handlingPhase_repaso') },
			{ value: 'gestacion', label: t('admin.lot.handlingPhase_gestacion') }
		]
	}

	const filteredPhaseOptions = phaseByHandling[state.handling] || [];

	const subPhaseByPhase = {
		postcubricion: [
			{ value: 'en_gestacion', label: t('admin.lot.subphase_en_gestacion') },
			{ value: 'en_preparto', label: t('admin.lot.subphase_en_preparto') },
			{ value: 'paridera', label: t('admin.lot.subphase_paridera') }
		],
		gestacion: [
			{ value: 'en_preparto', label: t('admin.lot.subphase_en_preparto') },
			{ value: 'paridera', label: t('admin.lot.subphase_paridera') }
		]
	}
	const filteredSubPhaseOptions = subPhaseByPhase[state.handlingPhase] || [];

	return (
		<Form onSubmit={submit} className="px-4 formulario">
			<Row className="my-2">
				<Col xs={12} md={6}>
					<Form.Group className="mb-3">
						<Form.Label>{t('admin.lot.identifier')}</Form.Label>
						<Form.Control
							type="text"
							name="identifier"
							value={state.identifier || ''}
							onChange={handleInputChange}
							required
						/>
					</Form.Group>
				</Col>
				<Col xs={12} md={6}>
					<Form.Group className="mb-3">
						<Form.Label>{t('admin.lot.type')}</Form.Label>
						<Select
							options={typeOptions}
							value={typeOptions.find(o => o.value === state.type)}
							onChange={option => handleInputChange({ target: { name: 'type', value: option.value } })}
							required
						/>
					</Form.Group>
				</Col>
			</Row>
			<Row className="my-2">
				<Col xs={12} md={6}>
					  <Form.Group className="mb-3">
						<Form.Label>{t('admin.lot.zone')}</Form.Label>
						<Select
							options={zones}
							value={state.zone ? zones?.find(option => option.value === state.zone._id) : null}
							onChange={option => handleInputChange({ target: { name: 'zone', value: option.value } })}
							required
						/>
					  </Form.Group>
				</Col>
				<Col xs={12} md={6}>
					<Form.Group className="mb-3">
						<Form.Label>{t('admin.lot.handling')}</Form.Label>
						<Select
							options={filteredHandlingOptions}
							value={handlingOptions.find(o => o.value === state.handling) || null}
							onChange={option => handleInputChange({ target: { name: 'handling', value: option.value } })}
							isDisabled={!state.type || filteredHandlingOptions.length === 0}
							required={filteredHandlingOptions.length > 0}
						/>
					</Form.Group>
				</Col>
			</Row>
			<Row className="my-2">
				<Col xs={12} md={6}>
					{filteredPhaseOptions.length > 0 && (
						<Form.Group className="mb-3">
							<Form.Label>{t('admin.lot.handlingPhase')}</Form.Label>
							<Select
							options={filteredPhaseOptions}
							value={filteredPhaseOptions.find(opt => opt.value === state.handlingPhase) || null}
							onChange={option => handleInputChange({ target: { name: 'handlingPhase', value: option.value } })}
							required
						/>
						</Form.Group>
					)}
				</Col>
				<Col xs={12} md={6}>
					{filteredSubPhaseOptions.length > 0 && (
						<Form.Group className="mb-3">
							<Form.Label>{t('admin.lot.subphase')}</Form.Label>
							<Select
							options={filteredSubPhaseOptions}
							value={filteredSubPhaseOptions.find(opt => opt.value === state.subphase) || null}
							onChange={option => handleInputChange({ target: { name: 'subphase', value: option.value } })}
						/>
						</Form.Group>
					)}
				</Col>
			</Row>
			<Row className="my-2">
				<Col xs={12}>
					<Form.Group className="mb-3">
						<Form.Label>{t('admin.lot.notes')}</Form.Label>
						<Form.Control
							as="textarea"
							name="notes"
							value={state.notes || ''}
							onChange={handleInputChange}
						/>
					</Form.Group>
				</Col>
			</Row>
			<Row>
				<Col xs={12} lg={6} className="my-2 ms-auto">
					<Button variant='primary' type="submit" className="w-100 my-2">
					{isRegistering ?
						<>
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</>
					:
						isAddingOrEditing === 'editing' ?
							<>{t('admin.lot.edit')}</>
						:
							<>{t('admin.lot.register')}</>
					}
					</Button>
				</Col>
			</Row>
		</Form>
	)
}
